import { MAX_RETRY } from '@root/private/js/common/securekey/constants';
import {
  decryptKey,
  generateExtraSecureKey,
  parseDecryptedData,
} from '@root/private/js/common/securekey/util';

import axios from 'axios';

import {
  DRM,
  JWPlayerConfig,
  PlayerTypeEnum,
} from '@src/modules/players/shared/interfaces/player-configs.interface';
import { openPromptForKey } from './password-prompt/open-key-prompt';
import { setDrmSource } from './drm/set-drm-source';
import { setExtraSecure } from './extra-secure/set-extra-secure';

declare const _playerOptions: JWPlayerConfig;

const isExtraSecurePlay = () => {
  const paths = window.location.pathname.split('/');
  return paths[paths.length - 1] === 'secure_ext';
};

export const checkSecureKey = async (
  secureKey: string,
  playerType: PlayerTypeEnum,
  retryCount = MAX_RETRY,
) => {
  const { label } = _playerOptions;
  const isExtraSecure = isExtraSecurePlay();

  // 1. 복호화 키 확보
  const secretKey = isExtraSecure
    ? generateExtraSecureKey(label)
    : await openPromptForKey(retryCount);

  // 2. secureKey 복호화
  const decrypted = decryptKey(secureKey, secretKey);
  if (!decrypted) {
    return {
      isError: true,
      message: 'Decryption failed. Please check your securekey or label.',
      errorType: isExtraSecure ? 'label' : '',
    };
  }

  // 3. 복호화된 데이터 파싱 → "playKey|expire[|action]"
  const [playKey, expire, action] = parseDecryptedData(decrypted, isExtraSecure);

  // 4. DRM 소스 설정
  await applyDrmSource({ playerType, playKey, expire, label });

  // 5. extraSecure 후처리 (동시 재생 방지 / 워터마크)
  if (isExtraSecure) {
    await setExtraSecure(action, label).catch(console.error);
  }

  return { isError: false };
};

type DrmMode = 'standard' | 'multi-label' | 'multi-password';

const resolveDrmMode = (isMultiDrm: boolean, label: string): DrmMode => {
  if (!isMultiDrm) return 'standard';
  if (label) return 'multi-label';
  return 'multi-password';
};

const applyDrmSource = async ({
  playerType,
  playKey,
  expire,
  label,
}: {
  playerType: PlayerTypeEnum;
  playKey: string;
  expire: string;
  label: string;
}) => {
  const isMultiDrm = _playerOptions.customOption.drm === DRM.MULTI_DRM;
  const drmMode = resolveDrmMode(isMultiDrm, label);

  if (drmMode === 'standard') {
    // 재생 URL에 playKey/expire 쿼리 파라미터 추가
    await setDrmSource(playerType, playKey, expire);
    return;
  }

  if (drmMode === 'multi-label') {
    // 서버에서 이미 소스 세팅됨, 프론트 처리 불필요
    return;
  }

  // drmMode === 'multi-password': 서버에서 KINX DRM 소스 발급 후 playlist에 주입
  await fetchAndInjectMultiDrmSources(playKey, expire);
};

const fetchAndInjectMultiDrmSources = async (playKey: string, expire: string) => {
  try {
    const { data } = await axios.post(`/drm`, {
      userId: _playerOptions.x_midibus_userid,
      objectId: _playerOptions.x_midibus_ob_id,
      key: playKey,
      expire,
    });
    _playerOptions.playlist = [{ sources: data }, ..._playerOptions.playlist];
  } catch (e) {
    console.error('확장 보안 멀티 DRM 소스 설정 에러: ' + e.toString());
  }
};